import React, { useEffect, useRef, useState } from "react";

import {
  AnyFilterExpressionInput,
  SortDirection,
  SortInput,
  TablesDetailsInput,
  useUpdateTableDetailsForUserMutation
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import { exportExcel, findObjectVal } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import { DEVICES_TABLE_NAME, OBJECTS_TABLE_NAME } from "../utils/const";
import { LazyState, TableColumn } from "../utils/types";
import { userTableDetails } from "../utils/utils";
import TableFilters from "./TableFilters";

type LazyTableProps = {
  data: any[];
  tableName: string;
  containerName: string;
  columns: TableColumn<any>[];
  loading: boolean;
  onLazyEvent: React.Dispatch<React.SetStateAction<LazyState>>;
  lazyState: LazyState;
  totalItems: number;
  fetchData: (
    take?: number | null,
    skip?: number | null,
    sort?: SortInput[] | null,
    filters?: AnyFilterExpressionInput[] | null,
    cacheAndNetwork?: boolean
  ) => void;
  buttons?: JSX.Element;
};

const LazyTable: React.FC<LazyTableProps> = ({
  data,
  tableName,
  containerName,
  columns,
  loading,
  onLazyEvent,
  lazyState,
  totalItems,
  fetchData,
  buttons,
}) => {
  const [excelData, setExcelData] = useState<any[]>([]);
  const [viewFilters, setViewFilters] = useState(false);

  const dt = useRef<DataTable>(null);
  const { userSettings } = useProjectContext();
  const tableDetails = userTableDetails(userSettings, tableName);

  useEffect(() => {
    if (tableDetails?.rows !== lazyState?.rows) {
      onLazyEvent(prev => ({ ...prev, rows: tableDetails?.rows || 10 }));
    }
  }, [userSettings]);

  useEffect(() => {
    if (data && data.length > 0) {
      setExcelData(data);
      dt.current?.reset();
    }
  }, [data]);

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();

  const handleExportExcel = () => {
    const tableData = excelData.map(row => {
      const newData: any = {};

      columns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field ? findObjectVal(row, col.field)?.toString() : col.body;
      });
      return newData;
    });

    exportExcel(tableData, tableName);
  };

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    if (tableDetails?.rows !== e.rows) {
      updateTableDetails({
        variables: { tableDetails: tableDetailsInput },
      });
    }
    onLazyEvent(prev => ({ ...prev, first: e.first, page: e.page }));
    fetchData(e.rows, e.first);
  };

  const onSort = (e: DataTablePFSEvent) => {
    onLazyEvent(prev => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder, first: 0 }));
    let property = e.sortField!;
    if (tableName === DEVICES_TABLE_NAME || tableName === OBJECTS_TABLE_NAME) {
      const properties = e.sortField.split(".");
      property =
        properties.length === 2 && properties.at(-1) === "value"
          ? `customFields.${properties[0]}`
          : e.sortField!;
    }
    const sort = [
      { property, direction: e.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc },
    ];
    fetchData(tableDetails?.rows , 0, sort);
  };

  return (
    <div className="card p-0 sm:p-3">
      <Toolbar
        left={
          <div className="flex gap-3">
            <Button
              label="Filters"
              icon={viewFilters ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              onClick={() => setViewFilters(prev => !prev)}
            />
          </div>
        }
        right={
          <div className="flex gap-3">
            <Button
              type="button"
              icon="pi pi-file"
              className="p-button-outlined p-button-warning"
              title="Export to CSV"
              onClick={exportCSV}
            />
            <Button
              type="button"
              icon="pi pi-file-excel"
              className="p-button-outlined p-button-warning"
              title="Export to Excel"
              onClick={handleExportExcel}
            />
            {buttons}
          </div>
        }
        className="gap-3"
      />

      <DataTable
        header={
          <div
            className={`${viewFilters ? "max-h-90rem" : "max-h-0"} overflow-hidden transition-all transition-duration-500 p-0`}
          >
            <TableFilters
              container={containerName}
              fetchData={fetchData}
              lazyState={lazyState}
              onLazyEvent={onLazyEvent}
            />
          </div>
        }
        ref={dt}
        value={data}
        onValueChange={tableData => {
          setExcelData(tableData);
        }}
        stripedRows
        paginator
        rows={lazyState.rows}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        loading={loading}
        size="small"
        lazy
        totalRecords={totalItems}
        className="overflow-x-auto lazy-table"
        first={lazyState.first || 0}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        onPage={onPage}
        onSort={onSort}
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};

export default LazyTable;
